import { Fragment, useState, type DragEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, Folder } from "@fg/icons";
import type { FolderRef } from "@/api/driveApi";
import { Menu, anchorFrom, type MenuAnchor } from "@/components/ui/menu";
import { cn } from "@/lib/utils";
import { isDriveDrag, isFileDrag, readDriveDrag } from "./items/dnd";

interface Crumb {
    id: string | null;
    name: string;
}

const hrefOf = (c: Crumb) => (c.id ? `/folder/${encodeURIComponent(c.id)}` : "/");

/**
 * My Drive › … › Parent › Current ▾. Every crumb navigates and accepts
 * drops (move items / upload files); deep paths collapse into a "…" menu;
 * the current folder opens its actions menu.
 */
export function Breadcrumbs({
    trail,
    folderId,
    currentName,
    onMoveDrop,
    onFilesDrop,
    onCurrentMenu
}: {
    /** Root→current chain from GET /api/items/{id}/ancestors (includes current). */
    trail: FolderRef[];
    folderId: string | null;
    currentName: string | null;
    onMoveDrop: (ids: string[], targetId: string | null) => void;
    onFilesDrop: (dt: DataTransfer, targetId: string | null) => void;
    onCurrentMenu: (el: HTMLElement) => void;
}) {
    const navigate = useNavigate();
    const [hover, setHover] = useState<string | null>(null);
    const [overflow, setOverflow] = useState<MenuAnchor | null>(null);

    const tail: Crumb[] = trail.length
        ? trail.map((f) => ({ id: f.id, name: f.name }))
        : folderId
          ? [{ id: folderId, name: currentName ?? "…" }]
          : [];
    const chain: Crumb[] = [{ id: null, name: "My Drive" }, ...tail];
    const current = chain[chain.length - 1]!;
    const ancestors = chain.slice(0, -1);
    let shown = ancestors;
    let hidden: Crumb[] = [];
    if (ancestors.length > 2) {
        hidden = ancestors.slice(1, -1);
        shown = [ancestors[0]!, ancestors[ancestors.length - 1]!];
    }

    const keyOf = (id: string | null) => id ?? "ROOT";
    const dropProps = (id: string | null) => ({
        onDragOver: (e: DragEvent<HTMLElement>) => {
            if (!isDriveDrag(e) && !isFileDrag(e)) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = isDriveDrag(e) ? "move" : "copy";
            setHover(keyOf(id));
        },
        onDragLeave: () => setHover((h) => (h === keyOf(id) ? null : h)),
        onDrop: (e: DragEvent<HTMLElement>) => {
            setHover(null);
            if (isDriveDrag(e)) {
                e.preventDefault();
                e.stopPropagation();
                const ids = readDriveDrag(e).filter((x) => x !== id);
                if (ids.length > 0) onMoveDrop(ids, id);
            } else if (isFileDrag(e)) {
                e.preventDefault();
                e.stopPropagation();
                onFilesDrop(e.dataTransfer, id);
            }
        }
    });

    const sep = (
        <span className="shrink-0 px-0.5 font-normal text-[var(--drive-text-3)]" aria-hidden>
            ›
        </span>
    );
    const crumbLink = (c: Crumb) => (
        <li className="min-w-0 shrink">
            <Link
                to={hrefOf(c)}
                {...dropProps(c.id)}
                className={cn(
                    "block max-w-[7rem] truncate rounded-[4px] px-1 py-0.5 font-normal text-[var(--drive-text-2)] transition-colors hover:bg-[var(--drive-hover)] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:max-w-[12rem]",
                    hover === keyOf(c.id) && "bg-[var(--drive-selected)] text-foreground ring-2 ring-primary"
                )}
                title={c.name}
            >
                {c.name}
            </Link>
        </li>
    );

    return (
        <nav aria-label="Breadcrumb" className="min-w-0">
            <ol className="flex min-w-0 items-center gap-1 text-base font-semibold leading-6 sm:text-[18px]">
                {shown[0] ? crumbLink(shown[0]) : null}
                {hidden.length > 0 ? (
                    <>
                        {sep}
                        <li className="shrink-0">
                            <button
                                type="button"
                                aria-label={`Show ${hidden.length} more folders`}
                                aria-haspopup="menu"
                                onClick={(e) => setOverflow(anchorFrom(e.currentTarget))}
                                className="rounded-[4px] px-1 py-0.5 font-normal text-[var(--drive-text-2)] transition-colors hover:bg-[var(--drive-hover)] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                                …
                            </button>
                        </li>
                    </>
                ) : null}
                {shown.slice(1).map((c) => (
                    <Fragment key={keyOf(c.id)}>
                        {sep}
                        {crumbLink(c)}
                    </Fragment>
                ))}
                {ancestors.length > 0 ? sep : null}
                <li className="min-w-0">
                    <button
                        type="button"
                        aria-haspopup="menu"
                        aria-current="page"
                        onClick={(e) => onCurrentMenu(e.currentTarget)}
                        {...dropProps(current.id)}
                        className={cn(
                            "flex min-w-0 items-center gap-1 rounded-[4px] px-1 py-0.5 font-semibold transition-colors hover:bg-[var(--drive-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                            hover === keyOf(current.id) && "bg-[var(--drive-selected)] ring-2 ring-primary"
                        )}
                        title={`${current.name} — folder actions`}
                    >
                        <span className="truncate">{current.name}</span>
                        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    </button>
                </li>
            </ol>
            {overflow ? (
                <Menu
                    label="Parent folders"
                    anchor={overflow}
                    onClose={() => setOverflow(null)}
                    entries={hidden.map((c) => ({
                        id: keyOf(c.id),
                        label: c.name,
                        icon: Folder,
                        iconClass: "text-amber-500",
                        onSelect: () => navigate(hrefOf(c))
                    }))}
                />
            ) : null}
        </nav>
    );
}
